if (!customElements.get('gf-carousel')) {
  customElements.define('gf-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.gf-carousel__track');
      this.controls = this.querySelector('.gf-carousel__controls');
      if (!this.track || !this.controls) return;
      this.previous = this.controls.querySelector('[data-direction="previous"]');
      this.next = this.controls.querySelector('[data-direction="next"]');
      this.onClick = event => {
        const button = event.target.closest('[data-direction]');
        if (!button || !this.controls.contains(button)) return;
        const direction = button.dataset.direction === 'previous' ? -1 : 1;
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        const amount = this.track.clientWidth * direction * (rtl ? -1 : 1);
        this.track.scrollBy({left: amount, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      };
      this.onUpdate = () => this.updateControls();
      this.onPlay = event => {
        if (event.target.tagName !== 'VIDEO') return;
        if (this.hasAttribute('data-silent-autoplay')) return;
        this.querySelectorAll('video').forEach(video => {
          if (video !== event.target) video.pause();
        });
      };
      this.onBlockSelect = event => {
        const card = event.target.closest('.gf-video-card, .gf-testimonial, .gf-customer-card');
        if (card && this.contains(card)) card.scrollIntoView({behavior: 'auto', block: 'nearest', inline: 'center'});
      };
      this.controls.addEventListener('click', this.onClick);
      this.track.addEventListener('scroll', this.onUpdate, {passive: true});
      this.addEventListener('play', this.onPlay, true);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      window.addEventListener('resize', this.onUpdate);
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this.onUpdate);
        this.resizeObserver.observe(this.track);
      }
      this.updateControls();
      if (this.hasAttribute('data-silent-autoplay')) this.setupSilentVideos();
    }
    setupSilentVideos() {
      this.videos = [...this.querySelectorAll('video')];
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.setSilent = video => {
        video.defaultMuted = true;
        if (!video.muted) video.muted = true;
        if (video.volume !== 0) video.volume = 0;
      };
      this.playSilent = video => {
        this.setSilent(video);
        const attempt = video.play();
        attempt?.catch(() => {});
      };
      this.onVideoVolume = event => {
        if (event.target.tagName === 'VIDEO') this.setSilent(event.target);
      };
      this.addEventListener('volumechange', this.onVideoVolume, true);
      this.videos.forEach(video => {
        this.setSilent(video);
        if (this.motionPreference.matches || window.IntersectionObserver) video.pause();
      });
      if (window.IntersectionObserver) {
        this.videoObserver = new IntersectionObserver(entries => {
          entries.forEach(({target: video, isIntersecting}) => {
            video.gfInView = isIntersecting;
            if (isIntersecting && !this.motionPreference.matches && !document.hidden) this.playSilent(video);
            else video.pause();
          });
        }, {threshold: 0.25});
        this.videos.forEach(video => this.videoObserver.observe(video));
      }
      this.onVideoVisibility = () => this.videos.forEach(video => {
        if (document.hidden || this.motionPreference.matches) video.pause();
        else if (video.gfInView || !this.videoObserver) this.playSilent(video);
      });
      document.addEventListener('visibilitychange', this.onVideoVisibility);
      this.motionPreference.addEventListener?.('change', this.onVideoVisibility);
    }
    updateControls() {
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const position = Math.abs(this.track.scrollLeft);
      this.controls.hidden = maximum <= 2;
      this.previous.disabled = position <= 2;
      this.next.disabled = position >= maximum - 2;
    }
    disconnectedCallback() {
      this.controls?.removeEventListener('click', this.onClick);
      this.track?.removeEventListener('scroll', this.onUpdate);
      this.removeEventListener('play', this.onPlay, true);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      window.removeEventListener('resize', this.onUpdate);
      this.resizeObserver?.disconnect();
      this.videoObserver?.disconnect();
      this.removeEventListener('volumechange', this.onVideoVolume, true);
      document.removeEventListener('visibilitychange', this.onVideoVisibility);
      this.motionPreference?.removeEventListener?.('change', this.onVideoVisibility);
      this.querySelectorAll('video').forEach(video => video.pause());
    }
  });
}

if (!customElements.get('gf-review-text')) {
  customElements.define('gf-review-text', class extends HTMLElement {
    connectedCallback() {
      this.text = this.querySelector('.gf-testimonial__text');
      this.button = this.querySelector('.gf-review-text__toggle');
      if (!this.text || !this.button) return;
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.expanded = false;
      this.onToggle = () => {
        this.expanded = !this.expanded;
        this.classList.toggle('gf-review-text--collapsed', !this.expanded);
        this.button.setAttribute('aria-expanded', String(this.expanded));
        this.button.textContent = this.expanded ? this.button.dataset.lessLabel : this.button.dataset.moreLabel;
      };
      this.onResize = () => this.update();
      this.button.addEventListener('click', this.onToggle);
      this.mobile.addEventListener?.('change', this.onResize);
      window.addEventListener('resize', this.onResize);
      if (window.ResizeObserver) {
        this.observer = new ResizeObserver(this.onResize);
        this.observer.observe(this.text);
      }
      this.update();
    }
    update() {
      if (!this.mobile.matches) {
        this.expanded = false;
        this.classList.remove('gf-review-text--collapsed');
        this.button.hidden = true;
        this.button.setAttribute('aria-expanded', 'false');
        this.button.textContent = this.button.dataset.moreLabel;
        return;
      }
      if (this.expanded) return;
      this.classList.add('gf-review-text--collapsed');
      this.button.hidden = this.text.scrollHeight <= this.text.clientHeight + 2;
      if (this.button.hidden) this.classList.remove('gf-review-text--collapsed');
    }
    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onToggle);
      this.mobile?.removeEventListener?.('change', this.onResize);
      window.removeEventListener('resize', this.onResize);
      this.observer?.disconnect();
    }
  });
}
